import { Tooltip } from '@patternfly/react-core';
import * as React from 'react';

import {
  AnswerTone,
  QuestionKind,
  QuestionSummary,
  SURVEY_SCORES,
  Submission,
  SurveyQuestion,
  SurveyView,
  answerTone,
  averageTone,
  booleanAnswer,
  computedNps,
  formatByKind,
  formatNps,
  npsGroup,
  numericAnswer,
  ratingFloor,
  ratingScale
} from '@app/model/Survey';

/**
 * The pieces the three survey layouts are built from.
 *
 * They are here rather than in the layouts because all three draw the same answer the same way -
 * an answer must not read as a 4 on one tab and a 4.0 on another, and a colour must mean the same
 * thing wherever it appears. Their styles are the `cas-survey-*` rules in app.css.
 *
 * **Colour is never the only thing that says what an answer was.** Every swatch carries the value
 * as text and a full sentence on its tooltip, so the grid is readable to somebody who cannot
 * distinguish the ramp, and a screen reader gets the question and the answer rather than "4".
 */

/** The class that paints a tone. Kept in one place so a renamed rule cannot be half-applied. */
const toneClass = (tone: AnswerTone): string => `cas-tone-${tone}`;

/** How an answer is said out loud: "Knew the subject: 5 out of 5". */
const spokenAnswer = (question: SurveyQuestion, value: number): string =>
  question.kind === 'nps'
    ? `${question.label}: ${value} out of 10 (${npsGroup(value)})`
    : `${question.label}: ${value} out of ${ratingScale(question.kind)}`;

interface AnswerCellProps {
  question: SurveyQuestion;
  submission: Submission;
}

/**
 * One respondent's answer to one question, as a matrix cell.
 *
 * A question the respondent skipped is drawn as an empty cell rather than as a zero or a dash in a
 * colour: a skip is the absence of an answer, and a zero would read as the bottom of a scale whose
 * bottom is 1. `instructorManage` and `instructorWebManage` mean a whole column of these is normal
 * - only one of the two is asked, and which depends on how the class was delivered.
 */
export const AnswerCell: React.FunctionComponent<AnswerCellProps> = ({ question, submission }) => {
  if (question.kind === 'yesno') {
    const answer = booleanAnswer(submission, question);
    if (answer === undefined) return <span className="cas-survey-cell cas-survey-cell-empty" aria-label="Not answered" />;
    return (
      <Tooltip content={`${question.label}: ${answer ? 'yes' : 'no'}`}>
        <span className={`cas-survey-cell ${answer ? 'cas-tone-yes' : 'cas-tone-no'}`}>
          {answer ? 'Y' : 'N'}
        </span>
      </Tooltip>
    );
  }

  const value = numericAnswer(submission, question);
  if (value === undefined) return <span className="cas-survey-cell cas-survey-cell-empty" aria-label="Not answered" />;

  return (
    <Tooltip content={spokenAnswer(question, value)}>
      <span className={`cas-survey-cell ${toneClass(answerTone(value, question.kind))}`}>{value}</span>
    </Tooltip>
  );
};

interface QuestionLabelProps {
  question: SurveyQuestion;
}

/**
 * A question's wording, with the field it really is on its tooltip.
 *
 * The field name is not decoration. Upstream sends field names and no question text - see
 * SURVEY_SECTIONS - so these labels are this application's reading of what was asked, and a reader
 * who knows the form needs to be able to see which field a label stands for before trusting it.
 */
export const QuestionLabel: React.FunctionComponent<QuestionLabelProps> = ({ question }) => (
  <Tooltip
    content={
      <>
        <div><code>{question.field}</code></div>
        {question.hint && <div className="cas-survey-hint">{question.hint}</div>}
      </>
    }
  >
    <span className="cas-survey-question">{question.label}</span>
  </Tooltip>
);

interface AverageProps {
  summary: QuestionSummary;
}

/**
 * A question's average over the submissions on hand, and how many of them answered it.
 *
 * The count is shown because it is not always the number of respondents: a question two of eight
 * skipped averages over six, and an average that quietly counted the skips as zero would be wrong
 * in the direction that matters. `(6/8)` is only drawn when the two differ, so a fully answered
 * question does not carry noise.
 */
export const AverageCell: React.FunctionComponent<AverageProps> = ({ summary }) => {
  if (summary.question.kind === 'yesno') {
    if (summary.count === 0) return <span className="cas-survey-muted">—</span>;
    return (
      <span>
        {summary.yes}/{summary.count} yes
      </span>
    );
  }

  if (summary.average === undefined) return <span className="cas-survey-muted">—</span>;

  return (
    <span>
      <span className={`cas-survey-average ${toneClass(averageTone(summary.average, summary.question.kind))}`}>
        {formatByKind(summary.average, summary.question.kind)}
      </span>
      {summary.count !== summary.asked && (
        <span className="cas-survey-muted"> ({summary.count}/{summary.asked})</span>
      )}
    </span>
  );
};

interface DistributionBarProps {
  summary: QuestionSummary;
}

/**
 * How a question's answers were spread, as one bar.
 *
 * Every possible answer is a segment, in ascending order, so the bars line up across questions and
 * a row that leans left is a bad question at a glance without reading a number. Only the answers
 * given are drawn - a value nobody chose contributes no segment rather than a zero-width one,
 * which would still cost a border.
 *
 * A segment is labelled with the answer *and* how many gave it - `5 ×3` - because the width
 * already suggests a count, and a segment reading just `5` beside a bar three eighths as wide is
 * read as "five people" about as often as "a five".
 *
 * Yes/no is deliberately not on the same ramp. "Contacted support: yes" is not a good answer or a
 * bad one, and painting it green would invent a judgement the question does not make.
 */
export const DistributionBar: React.FunctionComponent<DistributionBarProps> = ({ summary }) => {
  const { question, count } = summary;

  if (count === 0) {
    return <span className="cas-survey-muted">Nobody answered this</span>;
  }

  if (question.kind === 'yesno') {
    return (
      <span className="cas-survey-bar" role="img" aria-label={`${summary.yes} yes, ${summary.no} no`}>
        {summary.yes > 0 && (
          <Tooltip content={`Yes: ${summary.yes}`}>
            <span className="cas-survey-bar-part cas-tone-yes" style={{ flexGrow: summary.yes }}>
              Y<span className="cas-survey-bar-count">×{summary.yes}</span>
            </span>
          </Tooltip>
        )}
        {summary.no > 0 && (
          <Tooltip content={`No: ${summary.no}`}>
            <span className="cas-survey-bar-part cas-tone-no" style={{ flexGrow: summary.no }}>
              N<span className="cas-survey-bar-count">×{summary.no}</span>
            </span>
          </Tooltip>
        )}
      </span>
    );
  }

  const values: number[] = [];
  for (let value = ratingFloor(question.kind); value <= ratingScale(question.kind); value += 1) {
    if (summary.distribution.has(value)) values.push(value);
  }

  return (
    <span
      className="cas-survey-bar"
      role="img"
      aria-label={values.map(v => `${summary.distribution.get(v)} answered ${v}`).join(', ')}
    >
      {values.map(value => {
        const answers = summary.distribution.get(value) ?? 0;
        return (
          <Tooltip key={value} content={spokenAnswer(question, value) + ` — ${answers} of ${count}`}>
            <span
              className={`cas-survey-bar-part ${toneClass(answerTone(value, question.kind))}`}
              style={{ flexGrow: answers }}
            >
              {value}
              <span className="cas-survey-bar-count">×{answers}</span>
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
};

/**
 * Puts a class's Net Promoter Score on the 0-10 scale the tone buckets read, so the tile is
 * coloured the way the industry reads the index: 50 and above is excellent, 0 to 50 is fair, and
 * below zero - more detractors than promoters - is bad.
 *
 * A class score and a respondent's answer share a name and share nothing else. This is the one
 * place the two scales meet, and it is a mapping rather than a comparison.
 */
const npsToneValue = (score: number): number => (score >= 50 ? 10 : score >= 0 ? 7 : 0);

interface ScoreTileProps {
  label: string;
  value?: number;
  kind: QuestionKind;
  /** What the tile means, spelled out - all six are upstream's, which is worth saying once each. */
  description: string;
}

/**
 * One of the six scores the survey platform computed, as a tile.
 *
 * Shown with its scale (`/5`, or nothing for NPS, which is a -100..100 index and not out of
 * anything) because a bare "4.1" beside a bare "67" invites the reader to compare them.
 */
export const ScoreTile: React.FunctionComponent<ScoreTileProps> = ({ label, value, kind, description }) => {
  const tone = value === undefined ? undefined : averageTone(kind === 'nps' ? npsToneValue(value) : value, kind);
  return (
    <Tooltip content={description}>
      <div className={`cas-survey-tile ${tone ? toneClass(tone) : 'cas-survey-tile-empty'}`}>
        <div className="cas-survey-tile-value">
          {formatByKind(value, kind)}
          {kind !== 'nps' && value !== undefined && <span className="cas-survey-tile-scale">/5</span>}
        </div>
        <div className="cas-survey-tile-label">{label}</div>
      </div>
    </Tooltip>
  );
};

/** The ramp, said once, under whichever layout is using it. */
export const ToneLegend: React.FunctionComponent<{ showYesNo?: boolean }> = ({ showYesNo }) => (
  <div className="cas-survey-legend">
    <span className="cas-survey-muted">Ratings</span>
    <span className="cas-survey-cell cas-tone-bad">1</span>
    <span className="cas-survey-cell cas-tone-poor">2</span>
    <span className="cas-survey-cell cas-tone-fair">3</span>
    <span className="cas-survey-cell cas-tone-good">4</span>
    <span className="cas-survey-cell cas-tone-excellent">5</span>
    <span className="cas-survey-muted">NPS</span>
    <span className="cas-survey-cell cas-tone-bad">0-6</span>
    <span className="cas-survey-cell cas-tone-fair">7-8</span>
    <span className="cas-survey-cell cas-tone-excellent">9-10</span>
    {showYesNo && (
      <>
        <span className="cas-survey-muted">Yes/no</span>
        <span className="cas-survey-cell cas-tone-yes">Y</span>
        <span className="cas-survey-cell cas-tone-no">N</span>
      </>
    )}
    <span className="cas-survey-muted">
      <span className="cas-survey-cell cas-survey-cell-empty" /> not answered
    </span>
  </div>
);

/** What each of the six tiles means. All six are the survey platform's own figures. */
const SCORE_DESCRIPTIONS: Record<string, string> = {
  overallScore: "The survey platform's overall score for the class, out of 5.",
  instructorScore: "The survey platform's score for the instructor, out of 5.",
  curriculumScore: "The survey platform's score for the curriculum, out of 5.",
  labScore: "The survey platform's score for the labs, out of 5.",
  facilityScore: "The survey platform's score for the facility, out of 5.",
  nps: "The class's Net Promoter Score, from -100 to 100: the share of promoters less the share of detractors."
};

interface SurveyScoresProps {
  survey: SurveyView;
  /** The class's enrolment, for the response rate. Left out where the class is not to hand. */
  enrolled?: number;
  /**
   * The submissions, only to recompute the Net Promoter Score from them for comparison.
   *
   * Omitted by the class tab, which never reads submissions at all - so that line simply does not
   * appear there rather than appearing empty.
   */
  submissions?: readonly Submission[];
}

/**
 * The six scores and how many students answered: the header both the tab and the survey page wear.
 *
 * Shared so the two cannot drift into scoring the same class differently, which is the one
 * inconsistency a reader moving between them would actually notice.
 *
 * The response rate is submissions over enrolment and the two halves come from different places -
 * the count is what this service holds, the enrolment is the class's own - so a rate above 100% is
 * possible and means they disagree, not that something here is wrong.
 */
export const SurveyScores: React.FunctionComponent<SurveyScoresProps> = ({
  survey,
  enrolled,
  submissions
}) => {
  const rate = enrolled ? Math.round((survey.nrSubmissions / enrolled) * 100) : undefined;
  const ownNps = submissions ? computedNps(submissions) : undefined;

  return (
    <div className="cas-survey-header">
      <div className="cas-survey-tiles">
        {SURVEY_SCORES.map(({ field, label, kind }) => (
          <ScoreTile
            key={String(field)}
            label={label}
            kind={kind}
            value={survey[field] as number | undefined}
            description={SCORE_DESCRIPTIONS[String(field)] ?? label}
          />
        ))}
      </div>

      <div className="cas-survey-counts">
        <Tooltip content="How many submissions this service holds. The survey platform may hold more - it is not obliged to send every one, and a submission withdrawn upstream is not deleted here.">
          <span>
            <strong>{survey.nrSubmissions}</strong> submission{survey.nrSubmissions === 1 ? '' : 's'}
            {enrolled ? <span className="cas-survey-muted"> of {enrolled} enrolled ({rate}%)</span> : null}
          </span>
        </Tooltip>
        {ownNps !== undefined && (
          <Tooltip content="The Net Promoter Score of the answers held here, for comparison with the reported one above. They differ when this service does not hold every submission the survey platform does.">
            <span className="cas-survey-muted">NPS over these answers: {formatNps(ownNps)}</span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
